import type { Amounts, Family, Service } from './catalog.js'

// Ce module ne contient que des fonctions pures : aucune dépendance, aucun
// accès réseau ni base. Elles vivent dans `contracts` — et non côté site ou
// côté administration — parce que le site et le back-office doivent afficher
// exactement les mêmes prix. Les dupliquer rouvrirait la divergence que ce
// chantier ferme.

/**
 * Six situations pour cinq types : « séance partagée » et « cure » partagent le
 * type `quantite` et ne diffèrent que par l'unité. Les présenter séparément
 * évite d'exposer une abstraction que personne n'a demandée, et chaque choix
 * porte un exemple réel du catalogue.
 *
 * Vit ici, et non dans l'écran qui l'affiche, parce que trois appelants en
 * dépendent désormais : l'éditeur du catalogue (le sélecteur de situation),
 * l'éditeur des promotions (qui n'accepte que des prestations de même
 * situation) et l'API (qui refuse une promotion hétérogène). Les libellés sont
 * ce que la gérante lit dans les deux écrans : un second vocabulaire ailleurs
 * lui ferait croire à deux notions distinctes.
 */
export const SITUATIONS = [
  { cle: 'pass', libelle: 'Avec pass / sans pass', exemple: 'Aisselles — 7,50 € avec pass, 15 € sans' },
  { cle: 'duo', libelle: 'Seule ou à deux', exemple: 'Soin relaxant 30 min — 45 €/50 € seule, 95 € à deux' },
  { cle: 'quantite-personne', libelle: 'Séance partagée à plusieurs', exemple: 'Hammam 3 personnes — 70 € la séance, soit 23,33 €/pers.' },
  { cle: 'quantite-seance', libelle: 'Cure de plusieurs séances', exemple: 'Cure corps 6 séances — 280 €, soit 46,67 € la séance' },
  { cle: 'forfait', libelle: 'Séance à l’unité + forfait remisé', exemple: 'Lumière pulsée visage — 35 € la séance, 220 € les 8' },
  { cle: 'unique', libelle: 'Prix unique', exemple: 'Prêt natte — 1 €' },
] as const

export type Situation = (typeof SITUATIONS)[number]['cle']

export function situationDe(amounts: Amounts): Situation {
  if (amounts.type === 'quantite') return amounts.unite === 'personne' ? 'quantite-personne' : 'quantite-seance'
  return amounts.type
}

/** Libellé lisible d'une situation, tel qu'il apparaît dans les deux éditeurs. */
export function libelleSituation(situation: Situation): string {
  return SITUATIONS.find((s) => s.cle === situation)!.libelle
}

/** 2100 → « 21 € » ; 2333 → « 23,33 € ». Même règle que l'affichage actuel. */
export function formatCents(cents: number): string {
  const euros = cents / 100
  const texte = Number.isInteger(euros) ? String(euros) : euros.toFixed(2).replace('.', ',')
  return `${texte} €`
}

/** 1500 → « 15 » ; 750 → « 7,50 » ; null → « » (champ vide, tarif non
 * proposé). Réciproque de `saisieVersCents` pour l'affichage d'un champ
 * éditable — distincte de `formatCents`, qui ajoute « € » et ne convient donc
 * pas à la valeur d'un `<input>`. */
export function centsVersSaisie(cents: number | null): string {
  if (cents === null) return ''
  return cents % 100 === 0 ? String(cents / 100) : (cents / 100).toFixed(2).replace('.', ',')
}

/**
 * Centimes représentés par `texte`, si `texte` en représente un — y compris
 * une forme encore incomplète comme « 7, » (traitée comme « 7 », en
 * attendant les chiffres qui suivront). Renvoie `null` aussi bien pour un
 * texte vide que pour un texte qui ne contient encore aucun chiffre (« , »
 * seul) : à l'appelant de choisir, à l'aide de `respecteFormeMontant`, entre
 * ignorer la frappe (forme hors barème) et attendre la suite (vide, ou
 * séparateur sans chiffre derrière) — cette fonction ne fait volontairement
 * pas cette distinction elle-même.
 *
 * Toujours sûre même appelée directement sur un texte non gardé par
 * `respecteFormeMontant` (texte collé, appel depuis un test) : une forme hors
 * barème (espace, lettre, second séparateur, signe moins — voir
 * `respecteFormeMontant`) rend `null`, jamais un nombre extrait d'un préfixe
 * plausible comme le ferait `parseFloat`.
 */
export function saisieVersCents(texte: string): number | null {
  const correspondance = texte.match(/^(\d*)(?:[.,](\d{0,2}))?$/)
  if (!correspondance) return null
  const [, entiers, decimales] = correspondance
  if (entiers === '' && !decimales) return null
  const euros = entiers === '' ? 0 : Number.parseInt(entiers, 10)
  const centimes = !decimales ? 0 : decimales.length === 1 ? Number.parseInt(decimales, 10) * 10 : Number.parseInt(decimales, 10)
  return euros * 100 + centimes
}

/** Prix unitaire d'une prestation à quantité, arrondi au centime le plus proche. */
export function unitPriceCents(amounts: Amounts): number | null {
  if (amounts.type !== 'quantite') return null
  return Math.round(amounts.total / amounts.quantite)
}

/** Économie réalisée sur un forfait remisé, en centimes. */
export function packageSavingCents(amounts: Amounts): number | null {
  if (amounts.type !== 'forfait') return null
  return amounts.prixSeance * amounts.nombreSeances - amounts.prixForfait
}

/**
 * Vrai si un tarif « forfait » n'est pas dans un état que le schéma acceptera
 * à l'enregistrement : le prix du forfait doit être strictement inférieur au
 * prix à la séance multiplié par le nombre de séances (voir le superRefine de
 * `amountsSchema`, catalog.ts). Réduire le nombre de séances ou augmenter le
 * prix du forfait après la saisie initiale peut atteindre cet état.
 *
 * Vit ici, à côté de la règle qu'elle reflète, et non dans packages/admin où
 * vivait sa première version : c'est un prédicat de VALIDITÉ du domaine, pas
 * une fonction de rendu — `describeAmounts` ne le décrit pas, il suppose un
 * tarif déjà valide. Le seuil qu'elle applique coïncide exactement avec celui
 * du schéma (voir le test d'équivalence de ce module) ; le dupliquer aux deux
 * endroits rouvrirait précisément la divergence que ce fichier ferme (voir
 * son en-tête). Utilisée par l'aperçu de PricingFields et par le résumé
 * replié de ServiceRow (packages/admin) : les deux doivent s'accorder pour ne
 * jamais mettre en forme, avec l'autorité d'un résultat, une économie
 * négative que l'enregistrement rejettera.
 */
export function forfaitIncoherent(amounts: Amounts): boolean {
  return amounts.type === 'forfait' && packageSavingCents(amounts)! <= 0
}

/**
 * Libellé affiché d'une prestation. Le nom saisi l'emporte toujours ; à
 * défaut, et pour le seul type « quantite », il se compose de la quantité et
 * de l'unité. Cette règle reproduit à l'identique 23 des 24 noms du catalogue
 * d'origine, et évite que la quantité vive à deux endroits.
 */
export function computedServiceLabel(service: Pick<Service, 'name' | 'amounts'>): string {
  if (service.name !== null) return service.name
  const { amounts } = service
  if (amounts.type !== 'quantite') {
    // Interdit par serviceSchema ; défense en profondeur si la donnée vient
    // d'ailleurs (import, base modifiée à la main).
    return 'Prestation sans nom'
  }
  if (amounts.unite === 'personne') {
    return `${amounts.quantite} personne${amounts.quantite > 1 ? 's' : ''}`
  }
  return amounts.quantite > 1 ? `Forfait ${amounts.quantite} séances` : '1 séance'
}

/**
 * Description en texte simple d'un tarif, dans les mêmes termes que le rendu
 * du site. Sert l'aperçu de l'administration : la gérante voit le résultat
 * avant d'enregistrer, sans avoir à ouvrir le site.
 *
 * ATTENTION : cette fonction et le composant ServicePrice du site
 * (src/pages/Services/ServicePrice.jsx) doivent rester d'accord. Le site a
 * besoin de JSX pour sa typographie, d'où les deux formes ; le test de ce
 * module fige les huit textes attendus, et toute évolution du rendu doit
 * toucher les deux.
 */
export function describeAmounts(amounts: Amounts): string {
  switch (amounts.type) {
    case 'pass': {
      const { avecPass, sansPass } = amounts
      if (avecPass === null) return formatCents(sansPass!)
      if (sansPass === null) return `${formatCents(avecPass)} avec le pass`
      return `${formatCents(avecPass)} avec le pass · ${formatCents(sansPass)} sans`
    }
    case 'duo': {
      const { seuleAvecPass, seuleSansPass, aDeux } = amounts
      const parts: string[] = []
      const seule = seuleAvecPass ?? seuleSansPass!
      parts.push(`seule ${formatCents(seule)}${seuleAvecPass !== null ? ' avec le pass' : ''}`)
      if (seuleAvecPass !== null && seuleSansPass !== null) parts.push(`${formatCents(seuleSansPass)} sans`)
      if (aDeux !== null) parts.push(`à deux ${formatCents(aDeux)}`)
      return parts.join(' · ')
    }
    case 'quantite': {
      const unitaire = formatCents(unitPriceCents(amounts)!)
      return amounts.unite === 'personne'
        ? `${formatCents(amounts.total)} la séance, soit ${unitaire} par personne`
        : `${formatCents(amounts.total)}, soit ${unitaire} la séance`
    }
    case 'forfait':
      return (
        `${formatCents(amounts.prixSeance)} la séance · ` +
        `${formatCents(amounts.prixForfait)} les ${amounts.nombreSeances} séances ` +
        `(${formatCents(packageSavingCents(amounts)!)} d’économie)`
      )
    case 'unique': {
      const suffixe =
        amounts.unite === 'personne' ? ' par personne' : amounts.unite === 'seance' ? ' la séance' : ''
      return `${formatCents(amounts.prix)}${suffixe}`
    }
  }
}

/**
 * Montant retenu pour le badge « dès X € » : le moins cher qu'on puisse
 * réellement acheter. Jamais le tarif adhérent, jamais le prix à la séance
 * d'une cure qu'on ne peut acheter qu'entière.
 */
function comparableAmount(amounts: Amounts): { cents: number; perPerson: boolean } | null {
  switch (amounts.type) {
    // Aucun repli sur le tarif adhérent : une prestation réservée aux
    // détentrices du pass n'est achetable par personne d'autre, elle ne peut
    // donc pas fournir le « dès X € » d'une famille.
    case 'pass':
      return amounts.sansPass === null ? null : { cents: amounts.sansPass, perPerson: false }
    case 'duo':
      return amounts.seuleSansPass === null
        ? null
        : { cents: amounts.seuleSansPass, perPerson: false }
    case 'quantite':
      return amounts.unite === 'personne'
        ? { cents: unitPriceCents(amounts)!, perPerson: true }
        : { cents: amounts.total, perPerson: false }
    case 'forfait':
      // On peut acheter une séance seule : c'est le vrai point d'entrée.
      return { cents: amounts.prixSeance, perPerson: false }
    case 'unique':
      return { cents: amounts.prix, perPerson: amounts.unite === 'personne' }
  }
}

export function startingPrice(family: Family): { cents: number; perPerson: boolean } | null {
  const candidats = family.groups
    .filter((group) => group.visible)
    .flatMap((group) => group.services)
    .filter((service) => service.visible && !service.isExtra)
    .map((service) => comparableAmount(service.amounts))
    .filter((candidat): candidat is { cents: number; perPerson: boolean } => candidat !== null)

  if (candidats.length === 0) return null
  return candidats.reduce((min, candidat) => (candidat.cents < min.cents ? candidat : min))
}
